import type { CountrySlug } from "./countries";

export interface TripBlog {
  headline: string;      // H1
  intro: string;
  sections: {
    h2: string;
    body: string;
    subsections?: { h3: string; body: string; points?: { h4: string; body: string }[] }[];
  }[];
  bestTime: string;
  budget: string;
  duration: string;
  image: string;
}

export const TRIP_BLOGS: Record<CountrySlug, TripBlog> = {
  usa: {
    headline: "Plan Your Trip to the United States: The Ultimate 2026 Guide",
    intro:
      "From Manhattan's skyline to the rim of the Grand Canyon, the United States is a continent-sized adventure that punishes over-ambitious itineraries and rewards travellers who choose a theme and commit to it. This 2026 planning guide walks through entry paperwork, realistic regional routes, honest daily budgets, domestic transport, tipping and healthcare norms, connectivity, safety, and the seasonal quirks that decide whether your trip feels effortless or exhausting. Everything below is written for first-time visitors as well as returning travellers who want a sharper, better-value second trip.",
    bestTime: "April–June & September–October",
    budget: "$150–$300 / day",
    duration: "10–14 days",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f0/Brooklyn_Bridge_and_the_Lower_Manhattan_skyline_from_Pebble_Beach%2C_New_York.jpg/1280px-Brooklyn_Bridge_and_the_Lower_Manhattan_skyline_from_Pebble_Beach%2C_New_York.jpg",
    sections: [
      {
        h2: "Entry Requirements & Visa Options",
        body:
          "Every visitor needs either an ESTA under the Visa Waiver Program or a B1/B2 visitor visa stamped in a passport valid for the length of the stay. Start the paperwork 30 to 90 days before departure: ESTA approvals usually arrive within 72 hours, but B1/B2 interview slots in high-demand consulates can be booked out for months. Approval is never a guarantee of admission — the Customs and Border Protection officer at the port of entry makes the final decision, so carry your return ticket, accommodation bookings, and a short, honest explanation of your trip.",
        subsections: [
          {
            h3: "ESTA — Electronic System for Travel Authorization",
            body:
              "Valid for two years or until your passport expires, the ESTA allows stays of up to 90 days per entry for tourism, transit, or short business meetings. It costs $21, is filed only on the official CBP website, and cannot be extended or converted to another status from inside the country. Travellers who have visited certain restricted countries since 2011, or who hold dual nationality with them, lose ESTA eligibility and must apply for a full visa instead.",
            points: [
              { h4: "Apply Early", body: "File at least 72 hours before departure. Airlines check ESTA status at check-in and will deny boarding without an approval." },
              { h4: "Passport Changes", body: "A new passport voids an existing ESTA. Reapply immediately after any renewal or name change." },
            ],
          },
          {
            h3: "B1/B2 Tourist Visa",
            body:
              "Required for travellers outside the Visa Waiver Program. An in-person interview at a US embassy or consulate is mandatory for most first-time applicants, and wait times in 2026 range from around two weeks in low-volume posts to six months in South Asia. The visa is typically issued for multiple entries over five to ten years, though the admitted stay is decided at the border and usually capped at six months.",
            points: [
              { h4: "DS-160 Form", body: "Complete the online non-immigrant visa application, upload a compliant photo, and pay the MRV fee before you can book an interview slot." },
              { h4: "Financial Proof", body: "Bring three months of bank statements, salary slips, and tax filings showing you can fund the trip and have reasons to return home." },
              { h4: "Ties to Home", body: "Employment letters, property documents, and family responsibilities are the strongest evidence against a presumption of immigrant intent." },
            ],
          },
        ],
      },
      {
        h2: "Must-Visit Cities & Regions",
        body:
          "The United States offers wildly different experiences depending on the coast and the season. The classic mistake is a coast-to-coast sprint that turns a two-week holiday into a series of airport transfers. Instead, pick one themed corridor — urban east, sunbelt west, or national-park southwest — and add a single contrasting stop. Distances are the real constraint: New York to Los Angeles is roughly six hours in the air and three time zones, so treat each region as a separate trip.",
        subsections: [
          {
            h3: "East Coast Classics",
            body:
              "New York, Washington DC, Philadelphia, and Boston sit on a fast rail corridor and combine easily into a seven-day loop with almost no flying. Museums in Washington are free, Boston is compact enough to walk, and New York deserves at least three full days on its own.",
            points: [
              { h4: "Rail Between Cities", body: "Amtrak's Northeast Regional is cheaper than Acela and only 30–45 minutes slower on most legs." },
              { h4: "Museum Budget", body: "Smithsonian museums cost nothing, which offsets New York's $25–$30 admission prices." },
            ],
          },
          {
            h3: "West Coast Sun Belt",
            body:
              "Los Angeles to San Diego to San Francisco is the classic ten-day road trip, with Highway 1 and Big Sur as the scenic spine. Book a rental car with unlimited mileage, budget for $4–$6 per gallon of fuel, and expect city parking to add $30–$50 a night in downtown hotels.",
          },
          {
            h3: "National Parks Circuit",
            body:
              "The Grand Canyon, Zion, Bryce, Arches, and Yellowstone form the great American road-trip circuit. Fly into Las Vegas or Salt Lake City, buy the $80 America the Beautiful annual pass if you plan three or more parks, and reserve in-park lodging six to twelve months ahead for summer dates.",
            points: [
              { h4: "Timed Entry", body: "Several parks now use timed-entry or vehicle reservations in peak season. Check each park's official page the month you book flights." },
              { h4: "Altitude and Heat", body: "Zion and the Grand Canyon regularly exceed 38°C in July. Hike before 10am and carry three litres of water per person." },
            ],
          },
        ],
      },
      {
        h2: "Getting Around the Country",
        body:
          "Domestic flights on Southwest, Delta, JetBlue, and Alaska are frequently cheaper and always faster than long-distance rail or bus. Amtrak makes sense on the Northeast Corridor and for scenic routes such as the California Zephyr, but is slow elsewhere. Outside a dozen dense cities, public transport is limited and a rental car is effectively mandatory — including for national parks, wine regions, and most suburbs.",
        subsections: [
          { h3: "Renting a Car", body: "Most agencies require a credit card in the driver's name and charge $15–$30 a day for drivers under 25. An International Driving Permit is not required for short visits if your licence is in English, but it removes friction at the counter." },
          { h3: "City Transit", body: "New York, Chicago, Boston, Washington, and San Francisco have usable subway or metro systems that accept contactless bank cards. Los Angeles, Houston, and Atlanta are ride-share cities in practice." },
        ],
      },
      {
        h2: "Budgeting: What a US Trip Actually Costs",
        body:
          "Plan on $150 to $300 per person per day in 2026 for mid-range travel, excluding international flights. Accommodation is the biggest swing factor and the published rate is never the final rate: expect state and city lodging taxes of 12–18% plus a nightly resort or destination fee in Las Vegas, Orlando, and much of California. Restaurant menu prices exclude both sales tax and the 18–22% tip that servers depend on, so add roughly 30% to any printed figure.",
        subsections: [
          {
            h3: "Daily Cost Breakdown",
            body:
              "A realistic mid-range day looks like $130–$220 for a three-star hotel room, $60–$90 for three meals with tips, $25–$60 for transport or fuel and parking, and $30–$70 for attractions. Two people sharing a room cut the per-person cost by roughly a third.",
            points: [
              { h4: "Cheaper Months", body: "January to early March and late September to November deliver the lowest hotel rates outside ski and holiday destinations." },
              { h4: "City Passes", body: "Go City and CityPASS bundles pay off only if you visit three or more paid attractions in a day. Otherwise buy individually." },
            ],
          },
          {
            h3: "Money, Cards and Tipping",
            body:
              "Contactless cards and mobile wallets work almost everywhere, and cash is genuinely optional outside small-town diners and tips for housekeeping. Use a card with no foreign transaction fee, always decline the terminal's offer to charge in your home currency, and carry about $100 in small notes for tips and toll roads.",
            points: [
              { h4: "Standard Tips", body: "18–20% in restaurants, $1–$2 per drink at bars, $2–$5 per night for housekeeping, and 15–20% for taxi and ride-share." },
              { h4: "Sales Tax", body: "Ranges from zero in Oregon and Delaware to over 9% in parts of California and Tennessee, and is added at the till." },
            ],
          },
        ],
      },
      {
        h2: "Health, Insurance & Safety",
        body:
          "The single most important purchase before a US trip is comprehensive travel medical insurance with at least $100,000 of cover. There is no public safety net for visitors: an ambulance ride can cost $1,200 and a routine emergency-room visit for a sprain can exceed $3,000. Bring prescription medication in original labelled packaging with a copy of the prescription, and check that any controlled medicine is legal to import.",
        subsections: [
          { h3: "Everyday Safety", body: "Most tourist areas are safe, but conditions vary block by block in large cities. Use ride-share after dark rather than walking unfamiliar routes, never leave bags visible in a parked car, and follow local guidance about specific neighbourhoods." },
          { h3: "Weather Risks", body: "Hurricane season on the Atlantic and Gulf coasts runs June to November, wildfire smoke affects the West in late summer, and winter storms routinely close Midwest and Northeast airports. Refundable bookings are worth the premium in these windows." },
        ],
      },
      {
        h2: "Connectivity, Culture & Practical Etiquette",
        body:
          "An eSIM from a US carrier or a global provider is the simplest way to stay online: expect $25–$45 for 10–20GB over 30 days, with far better coverage from the major networks in rural and park areas. Free Wi-Fi is standard in hotels, cafés, and most airports. Socially, Americans are direct, friendly with strangers, and punctual; small talk with staff is normal rather than intrusive, and service culture is fast and attentive because of tipping.",
        subsections: [
          { h3: "Plugs and Power", body: "120V at 60Hz with Type A and B sockets. Most laptop and phone chargers are dual-voltage, but hair dryers and shavers from 230V countries usually are not." },
          { h3: "Alcohol and ID", body: "The legal drinking age is 21 everywhere and photo ID is checked regardless of apparent age. Open containers in public are illegal in most states." },
        ],
      },
      {
        h2: "Frequently Asked Questions",
        body:
          "The questions below are the ones readers send us most often before a first trip to the United States, answered with 2026 rules and prices.",
        subsections: [
          {
            h3: "Planning and paperwork",
            body: "Short answers to the entry and timing questions that shape the rest of the itinerary.",
            points: [
              { h4: "How far ahead should I book?", body: "Flights three to five months out, national-park lodging six to twelve months out, and city hotels six to eight weeks out for the best balance of price and choice." },
              { h4: "Is 90 days on an ESTA extendable?", body: "No. The 90-day limit is firm and cannot be extended or changed to another status from inside the country." },
              { h4: "Do I need an onward ticket?", body: "Yes for ESTA travellers. Airlines and border officers routinely ask for proof of departure within the permitted period." },
            ],
          },
          {
            h3: "On the ground",
            body: "Everyday practicalities that surprise first-time visitors.",
            points: [
              { h4: "Can I drive on my home licence?", body: "Generally yes for visits under 90 days if the licence is in English; otherwise carry an International Driving Permit alongside it." },
              { h4: "Is tap water safe?", body: "Yes in virtually all cities and parks. Carry a refillable bottle — fountains are common in airports and trailheads." },
              { h4: "What about domestic flight security?", body: "Arrive two hours before departure. Liquids follow the 100ml rule and laptops usually come out of bags unless you have TSA PreCheck." },
            ],
          },
        ],
      },
    ],
  },
  uk: {
    headline: "Plan Your Trip to the United Kingdom: London, Scotland & Beyond",
    intro:
      "Britain packs two thousand years of history, four distinct nations, and some of Europe's most dramatic coastline into an island roughly the size of Michigan. That compactness is the planning advantage: you can breakfast in London, walk a Roman wall in Northumberland the next morning, and be in the Scottish Highlands by evening. This 2026 guide covers the new ETA entry rules, a workable seven-to-ten-day route, rail booking tactics that halve ticket prices, honest daily budgets in pounds, weather realities, and the etiquette that makes travel here smoother.",
    bestTime: "May–September",
    budget: "£120–£250 / day",
    duration: "7–10 days",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f6/City_of_London_skyline_from_London_City_Hall_-_Oct_2008.jpg/1280px-City_of_London_skyline_from_London_City_Hall_-_Oct_2008.jpg",
    sections: [
      {
        h2: "Visa & Entry Requirements",
        body:
          "Most Western travellers still receive up to six months of visa-free entry, but the paperwork changed in 2025–26: visa-exempt nationalities now need an Electronic Travel Authorisation before boarding. Everyone else applies for a Standard Visitor Visa in advance. Passports must be valid for the whole stay, and eGates at major airports are open to a growing list of nationalities, which cuts arrival queues from an hour to a few minutes.",
        subsections: [
          {
            h3: "UK ETA Application",
            body:
              "The ETA costs £10, is valid for two years or until passport expiry, permits multiple visits of up to six months, and is usually approved within three working days through the official UK ETA app. Apply well before travel — a small share of applications are referred for manual review that takes longer.",
            points: [
              { h4: "Who Needs One", body: "Visa-exempt visitors including EU, US, Canadian, Australian, and Gulf nationals. Irish citizens and UK residence-permit holders are exempt." },
              { h4: "Transit Passengers", body: "Most airside transit passengers at UK airports also need an ETA. Check before booking a connecting itinerary through Heathrow." },
            ],
          },
          {
            h3: "Standard Visitor Visa",
            body:
              "For nationalities that require a visa, this allows six-month stays for tourism, business meetings, or visiting family, with long-term two-, five-, and ten-year options for frequent travellers. Biometrics are collected at a visa application centre and standard processing runs about three weeks.",
            points: [
              { h4: "Documents", body: "Bank statements covering six months, an itinerary, accommodation bookings, and an invitation letter if visiting family." },
              { h4: "Priority Service", body: "Paid priority processing cuts decisions to about five working days where available." },
            ],
          },
        ],
      },
      {
        h2: "Top Destinations & Regions",
        body:
          "Britain's real appeal is the contrast between dense historic cities and empty countryside that begins twenty minutes outside them. A satisfying first trip pairs three or four days in London with one northern city and one rural region, rather than attempting England, Wales, and Scotland in a week.",
        subsections: [
          {
            h3: "London",
            body:
              "Allow three to four full days for the free national museums, the West End, and the neighbourhoods that give the city its character — Shoreditch for markets, Notting Hill for terraces and antiques, Greenwich for maritime history and the best skyline view. Book Westminster Abbey, the Tower of London, and any major exhibition online in advance.",
            points: [
              { h4: "Free Museums", body: "The British Museum, National Gallery, Tate Modern, V&A, and Natural History Museum charge nothing for permanent collections." },
              { h4: "Getting Around", body: "Tap in and out with a contactless card; daily and weekly capping means you never overpay and never need a paper ticket." },
            ],
          },
          {
            h3: "Edinburgh & the Highlands",
            body:
              "The LNER train from London to Edinburgh takes about four and a half hours and is far more pleasant than flying. Base yourself in the Old Town for two nights, then hire a car or join a small-group tour for Glencoe, Loch Ness, and the Isle of Skye. August means the Fringe: extraordinary atmosphere, double accommodation prices.",
          },
          {
            h3: "The Cotswolds, Bath & Wales",
            body:
              "Rent a car in Oxford or Bath for the honey-stone villages of Chipping Campden, Bibury, and Castle Combe — public transport here is sparse. Add Cardiff and the Brecon Beacons for a Welsh leg, or Snowdonia if you want mountains within three hours of Manchester.",
            points: [
              { h4: "Driving Note", body: "Country lanes are single-track with passing places. Choose the smallest rental car you can live with." },
              { h4: "Village Timing", body: "Arrive before 10am or after 5pm in summer to see the famous villages without coach crowds." },
            ],
          },
        ],
      },
      {
        h2: "Transport, Rail Fares & Money",
        body:
          "The rail network is excellent and expensive if booked late. Advance tickets released twelve weeks before departure routinely cost half the walk-up fare, and a £30 Railcard returns a third off most journeys for a year — worth it after roughly three intercity trips. Contactless payment works on the Tube, London buses, and increasingly in Manchester, Liverpool, and the West Midlands.",
        subsections: [
          {
            h3: "Trains, Coaches and Cars",
            body:
              "Trainline and the operators' own apps show identical fares; split-ticketing tools can shave another 10–30% on long routes. National Express and Megabus coaches are far cheaper and roughly twice as slow. Rent a car only for rural regions, and remember Britain drives on the left with tight rural lanes.",
            points: [
              { h4: "London Congestion Charge", body: "£15 per day inside the central zone, plus ULEZ charges for non-compliant vehicles. Avoid driving into London entirely." },
              { h4: "Airport Transfers", body: "The Elizabeth line to Heathrow costs a fraction of the Heathrow Express and takes only 15 minutes longer." },
            ],
          },
          {
            h3: "Cards, Cash and Tipping",
            body:
              "The UK is close to cashless: cards work on buses, in market stalls, and in village pubs. Tipping is modest and never obligatory — 10–12.5% in restaurants if service is not already added, rounding up for taxis, and nothing at all at the bar.",
          },
        ],
      },
      {
        h2: "Budgeting a British Holiday",
        body:
          "Expect £120–£250 per person per day for mid-range travel. London runs 30–40% above the national average, while northern cities, Wales, and rural Scotland offer noticeably better value. Accommodation dominates the budget: a decent central London room is £160–£240 a night in summer, against £90–£140 in Manchester, Glasgow, or Bristol.",
        subsections: [
          {
            h3: "Where the Money Goes",
            body:
              "A typical day is £110–£200 for accommodation, £45–£70 for food, £15–£30 for local transport, and £20–£45 for paid attractions. Pub lunches, supermarket meal deals, and free national museums are the three biggest levers for cutting costs without cutting experiences.",
            points: [
              { h4: "Shoulder Season", body: "Late April to mid-May and late September deliver good weather, long days, and 20–30% lower hotel rates than July." },
              { h4: "Heritage Passes", body: "English Heritage and National Trust touring passes pay for themselves after four or five properties." },
            ],
          },
          {
            h3: "Weather and Packing",
            body:
              "Summer highs sit around 18–24°C with genuine rain risk on any day, and Scottish weather can shift within an hour. Pack layers, a compact waterproof shell, and comfortable shoes for cobblestones — an umbrella is less useful than a hood in coastal wind.",
          },
        ],
      },
      {
        h2: "Food, Culture & Etiquette",
        body:
          "British food has changed beyond its old reputation: Britain's cities offer some of Europe's best South Asian, Middle Eastern, and Caribbean cooking, and gastropubs serve serious regional produce. Queueing is close to sacred, personal space is respected, and 'sorry' is used as punctuation rather than apology. Sunday roast is a genuine cultural institution and worth building into an itinerary.",
        subsections: [
          { h3: "Eating Well on a Budget", body: "Weekday set lunches, market halls in Manchester and Cardiff, and supermarket meal deals at around £4 keep food costs low without resorting to chains." },
          { h3: "Opening Hours", body: "Many rural attractions close by 5pm and some shut entirely from November to March. Sunday trading is limited to six hours for large stores." },
        ],
      },
      {
        h2: "Frequently Asked Questions",
        body:
          "Common reader questions about visiting the UK in 2026, with current prices and rules.",
        subsections: [
          {
            h3: "Entry and timing",
            body: "The paperwork and seasonal questions that come up most.",
            points: [
              { h4: "Does the ETA guarantee entry?", body: "No. It authorises travel to the border; a Border Force officer still decides admission and duration." },
              { h4: "Is a week enough?", body: "Yes for London plus one other region. Add three days if Scotland is on the list." },
              { h4: "Can I visit Ireland on the same trip?", body: "Ireland is outside the UK and has separate entry rules, though many nationalities enter both visa-free. Check both before booking." },
            ],
          },
          {
            h3: "Money and logistics",
            body: "Practical answers for day-to-day spending and movement.",
            points: [
              { h4: "Do I need cash?", body: "Rarely. A contactless card covers almost everything; carry £20–£30 for small rural cafés and parking meters." },
              { h4: "Are trains cheaper than flying?", body: "Within Britain, almost always once you count airport transfers and baggage fees." },
              { h4: "Is tap water safe?", body: "Yes across the UK, and restaurants must supply it free on request." },
            ],
          },
        ],
      },
    ],
  },
  canada: {
    headline: "Plan Your Trip to Canada: Rockies, Cities & Northern Lights",
    intro:
      "Canada is the second-largest country on earth, and each region feels like a different trip: the Rockies are alpine and outdoorsy, Quebec is francophone and European, the Maritimes are maritime and slow, and the territories deliver subarctic wilderness under the aurora. This 2026 guide helps you decide what is realistic in a single visit, with eTA rules, east-versus-west routes, seasonal trade-offs, driving distances, wildlife safety, and a clear-eyed daily budget in Canadian dollars.",
    bestTime: "June–September (summer) or December–March (ski/aurora)",
    budget: "C$180–$320 / day",
    duration: "10–14 days",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f4/Banff_National_Park_%28AB%2C_Canada%29%2C_Lake_Louise_--_2022_--_2197.jpg/1280px-Banff_National_Park_%28AB%2C_Canada%29%2C_Lake_Louise_--_2022_--_2197.jpg",
    sections: [
      {
        h2: "eTA & Visa Information",
        body:
          "Visa-exempt air travellers need an eTA, which costs C$7, is valid five years, and links electronically to the passport used in the application. Everyone else applies for a Temporary Resident Visa with biometrics. Both are filed only on the official IRCC website — the paid copycat sites that dominate search results charge many times the real fee for the same form.",
        subsections: [
          {
            h3: "eTA Eligibility",
            body:
              "Most European, UK, Australian, Japanese, and South Korean passport holders qualify, along with certain visa-exempt travellers arriving by air. Approvals are typically instant, though a minority are referred for document checks that take several days. An eTA is not required when entering by land or sea from the United States.",
            points: [
              { h4: "Official Site Only", body: "Apply through canada.ca. Third-party services charge C$50–$100 for an identical outcome." },
              { h4: "Passport Linked", body: "A renewed passport requires a new eTA even if the old one has years left." },
            ],
          },
          {
            h3: "Temporary Resident Visa (TRV)",
            body:
              "Single or multiple entry, with biometrics collected at a visa application centre and processing that ranges from two to twelve weeks depending on country. Applicants show funds, travel history, employment, and ties to home. A US visitor visa can simplify transit itineraries but does not replace the TRV.",
            points: [
              { h4: "Funds Guidance", body: "Roughly C$150–$200 per day of the planned stay, evidenced by six months of statements." },
              { h4: "Family Visits", body: "An invitation letter with the host's status documents materially strengthens the application." },
            ],
          },
        ],
      },
      {
        h2: "Best Regions to Visit",
        body:
          "Choose east or west. The Toronto–Vancouver flight is roughly five hours and crosses three time zones, so combining both in ten days means two travel days and constant repacking. Both halves offer city, mountain, and water; the difference is language, landscape scale, and price.",
        subsections: [
          {
            h3: "Western Canada",
            body:
              "Vancouver to Banff to Jasper along the Icefields Parkway is one of the world's great drives, with glacier viewpoints, turquoise lakes, and frequent wildlife sightings. Allow at least seven days: three in the Rockies, two in Vancouver, and two for driving and Whistler.",
            points: [
              { h4: "Banff National Park", body: "Lake Louise and Moraine Lake peak in July and are now served by shuttles and reservations rather than private cars. June and September are quieter and cheaper." },
              { h4: "Whistler", body: "World-class skiing December to April; mountain biking, hiking, and via ferrata in summer, at about half the winter room rate." },
              { h4: "Park Fees", body: "A Parks Canada Discovery Pass costs around C$75 per adult per year and beats daily entry after about seven days." },
            ],
          },
          {
            h3: "Eastern Canada",
            body:
              "Toronto, Niagara Falls, Ottawa, Montreal, and Quebec City form a compact corridor connected by VIA Rail and a five-hour drive end to end. Mid-October fall foliage in the Laurentians and Eastern Townships is genuinely world class, and Quebec City in winter is a snow-globe city with the Carnaval as its centrepiece.",
            points: [
              { h4: "Rail Corridor", body: "VIA Rail's Toronto–Montreal–Quebec route is comfortable and often cheaper than flying when booked early." },
              { h4: "Language", body: "French is the working language of Quebec. A few polite phrases are appreciated; English is widely understood in tourist areas." },
            ],
          },
        ],
      },
      {
        h2: "Northern Lights & Winter Travel",
        body:
          "Yellowknife in the Northwest Territories and Whitehorse in the Yukon sit under the auroral oval and deliver the highest success rates in North America. The season runs late August to mid-April, and three consecutive nights out gives roughly a 90% chance of a sighting. Winter temperatures of −30°C are normal, so operators supply insulated suits and boots as part of the tour price.",
        subsections: [
          { h3: "Planning an Aurora Trip", body: "Book near a new moon, stay a minimum of three nights, and choose a lodge or tour with heated viewing shelters. Expect C$150–$250 per night of guided viewing on top of accommodation." },
          { h3: "Winter Driving", body: "Rentals in mountain and northern regions come with winter tyres by law in some provinces. Carry a charged phone, blanket, and food; mobile coverage disappears between towns." },
        ],
      },
      {
        h2: "Costs, Money & Tipping",
        body:
          "Budget C$180–$320 per person per day for mid-range travel. Rockies accommodation in July is the single biggest expense and can exceed C$350 a night in Banff townsite; staying in Canmore or Golden cuts that sharply. Sales taxes of 5–15% are added at the till and vary by province, and tipping of 15–20% is expected in restaurants and for guided tours.",
        subsections: [
          {
            h3: "Typical Daily Spend",
            body:
              "Plan on C$140–$260 for lodging, C$60–$90 for food, C$40–$80 for a rental car with fuel, and C$25–$60 for activities and park passes. Groceries and picnic lunches are the easiest saving in national parks, where restaurant options are limited and priced accordingly.",
            points: [
              { h4: "Shoulder Season", body: "June and September in the Rockies mean 25–40% lower rates and far easier parking at trailheads." },
              { h4: "Booking Lead Time", body: "Reserve summer Banff and Jasper accommodation six to nine months ahead; it genuinely sells out." },
            ],
          },
          {
            h3: "Health and Insurance",
            body:
              "Visitors are not covered by provincial health insurance and hospital charges are billed at full cost. Travel medical insurance with evacuation cover is essential, particularly for backcountry hiking, skiing, or northern travel where a medevac flight is the only option.",
          },
        ],
      },
      {
        h2: "Wildlife, Weather & Outdoor Safety",
        body:
          "Bears, elk, and moose are common along mountain highways and in campgrounds. Carry bear spray on Rockies trails, know how to use it, keep 100 metres from bears and 30 from elk, and never store food in a tent. Weather in the mountains changes fast: snow is possible in every month above 2,000 metres, and afternoon thunderstorms are routine in July and August.",
        subsections: [
          { h3: "Trail Preparation", body: "Register your plan with someone, check Parks Canada trail reports for closures, and carry layers, water, and a headlamp even on short hikes." },
          { h3: "Connectivity", body: "A Canadian eSIM costs about C$30–$50 for 10–20GB. Coverage is strong in cities and along major highways but absent in much of the backcountry and the territories." },
        ],
      },
      {
        h2: "Frequently Asked Questions",
        body:
          "The questions readers ask most before a first Canadian trip, answered for 2026 conditions.",
        subsections: [
          {
            h3: "Routing and season",
            body: "Deciding when to go and how much ground to cover.",
            points: [
              { h4: "East or west for a first visit?", body: "West for mountains and lakes, east for cities, history, and French culture. Ten days comfortably covers one, not both." },
              { h4: "Is winter worth it?", body: "Yes for skiing, aurora, and Quebec's winter festivals — but daylight is short and driving needs care." },
              { h4: "Do I need a car?", body: "Essential in the Rockies and Maritimes; unnecessary in Toronto, Montreal, and downtown Vancouver." },
            ],
          },
          {
            h3: "Practical details",
            body: "Money, crossings, and everyday logistics.",
            points: [
              { h4: "Can I hop into the USA?", body: "Only with valid US entry documents — an ESTA or visa. Canadian status confers nothing at the US border." },
              { h4: "Is tap water safe?", body: "Yes nationwide, and refill stations are common in parks and airports." },
              { h4: "How much cash?", body: "Very little. Cards are universal; keep C$50 for small-town parking and tips." },
            ],
          },
        ],
      },
    ],
  },
  australia: {
    headline: "Plan Your Trip to Australia: Reef, Outback & Coastal Cities",
    intro:
      "Australia rewards travellers who slow down. It is roughly the size of continental Europe with the population of a mid-sized country, so the distances between headline sights are measured in flight hours rather than train rides. Given how far most visitors fly to get here, two to three weeks is the honest minimum for a satisfying first trip. This 2026 guide covers ETA and working-holiday visas, the regions worth prioritising, internal transport, reef and outback safety, sun protection, and realistic costs in Australian dollars.",
    bestTime: "September–November & March–May",
    budget: "A$200–$350 / day",
    duration: "14–21 days",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Sydney_%28AU%29%2C_Opera_House_--_2019_--_3054.jpg/1280px-Sydney_%28AU%29%2C_Opera_House_--_2019_--_3054.jpg",
    sections: [
      {
        h2: "ETA, eVisitor & Visitor Visas",
        body:
          "Australia has no visa-free entry: every non-citizen needs an authorisation before boarding, even for a two-day stopover. Which one depends on nationality. The good news is that most are online-only, approved within days, and valid for a full year of multiple visits, which makes a stopover on the way to New Zealand or Asia easy to add.",
        subsections: [
          {
            h3: "ETA (subclass 601) and eVisitor (subclass 651)",
            body:
              "The ETA costs A$20 through the official Australian ETA app and suits passport holders from the US, Canada, Japan, Singapore, and several other countries. The eVisitor is free for eligible European passport holders. Both are valid twelve months and allow repeated stays of up to three months each.",
            points: [
              { h4: "App Only", body: "The ETA can only be lodged in the government app, not on a website. Have the passport and a card ready." },
              { h4: "No Work Rights", body: "Neither permits paid work. Volunteering and business meetings are allowed; employment is not." },
            ],
          },
          {
            h3: "Visitor Visa (subclass 600) and Working Holiday (417/462)",
            body:
              "Nationalities outside the ETA and eVisitor lists apply for the subclass 600 visitor visa, typically for three, six, or twelve months. Travellers aged 18–35 from partner countries can instead use a Working Holiday visa to fund a long trip with casual work in hospitality, farming, or tourism.",
            points: [
              { h4: "Health Checks", body: "Stays longer than six months, or work in certain settings, can trigger a medical examination requirement." },
              { h4: "Second-Year Extension", body: "Specified regional work can extend a Working Holiday visa for a second and even third year." },
            ],
          },
        ],
      },
      {
        h2: "Top Regions to Prioritise",
        body:
          "Cover two or three regions well. Trying to see Sydney, the reef, Uluru, Melbourne, and Perth in one trip means five domestic flights and most of your holiday in airports. Match the region to the season: the tropical north is best in the dry season from May to October, while the southern states shine from November to March.",
        subsections: [
          {
            h3: "Sydney & New South Wales",
            body:
              "Base here four to five days for the harbour, Bondi to Coogee coastal walk, and the Opera House. Add a Blue Mountains day for eucalyptus canyons and a Hunter Valley day for wine. Sydney's ferries are the cheapest sightseeing in the country.",
            points: [
              { h4: "Opal Card or Card Tap", body: "Contactless bank cards work directly on trains, buses, and ferries with daily and weekly caps." },
              { h4: "Beach Safety", body: "Swim between the red and yellow flags — patrolled beaches only, and never alone at dusk." },
            ],
          },
          {
            h3: "Great Barrier Reef & Tropical Queensland",
            body:
              "Fly to Cairns and dedicate at least two full days to the outer reef from Port Douglas or Cairns, ideally with a small-vessel operator. Pair it with the Daintree Rainforest, the world's oldest tropical rainforest, and Cape Tribulation where reef meets jungle.",
            points: [
              { h4: "Stinger Season", body: "November to May requires a full-body stinger suit for ocean swimming in the tropical north; operators supply them." },
              { h4: "Reef Tax", body: "A small Environmental Management Charge is added to every reef trip and funds marine park management." },
            ],
          },
          {
            h3: "Uluru & the Red Centre",
            body:
              "A three-day loop from Alice Springs or a direct flight to Ayers Rock airport covers Uluru, Kata Tjuta, and Kings Canyon. Sunrise at Uluru is the highlight of many trips. Climbing is permanently closed out of respect for Anangu traditional owners; the base walk is the experience.",
            points: [
              { h4: "Heat Planning", body: "Summer highs exceed 40°C and trails close by 11am. May to September is far more comfortable." },
              { h4: "Accommodation", body: "Yulara is the only resort area and sells out months ahead in peak season." },
            ],
          },
        ],
      },
      {
        h2: "Getting Around a Continent",
        body:
          "Domestic flights on Qantas, Virgin Australia, and Jetstar are the only realistic way to cover long distances — Sydney to Cairns is three hours in the air and twenty-four by road. Within regions, a rental car unlocks the Great Ocean Road, Tasmania, and the Margaret River. Long-distance rail exists as an experience rather than transport: the Ghan and Indian Pacific are multi-day journeys priced as cruises.",
        subsections: [
          { h3: "Driving Distances", body: "Plan a maximum of five hours of driving a day, fuel up whenever you see a station in remote areas, and avoid driving at dawn or dusk when kangaroos are active." },
          { h3: "City Transit", body: "Sydney, Melbourne, Brisbane, and Perth all have workable train and tram networks with contactless payment. Melbourne's central tram zone is free." },
        ],
      },
      {
        h2: "Costs, Money & Seasons",
        body:
          "Australia is expensive by global standards: A$200–$350 per person per day is realistic for mid-range travel, driven by accommodation, domestic flights, and tours. The compensation is that there is no tipping culture, wages are built into menu prices, and public beaches, coastal walks, and most galleries are free.",
        subsections: [
          {
            h3: "Daily Budget Breakdown",
            body:
              "Expect A$150–$250 for a mid-range room, A$70–$110 for food, A$40–$90 amortised for domestic flights or car hire, and A$60–$180 on days with a reef trip or guided tour. Booking domestic flights three months out roughly halves the fare.",
            points: [
              { h4: "No Tipping Needed", body: "Rounding up for exceptional service is optional. Nothing is expected in cafés, taxis, or bars." },
              { h4: "GST Refund", body: "The Tourist Refund Scheme returns 10% GST on eligible goods over A$300 bought within 60 days of departure." },
            ],
          },
          {
            h3: "Seasons Upside Down",
            body:
              "December to February is summer: hot, humid in the north, and peak-priced everywhere because of school holidays. September to November and March to May give warm days, lower prices, and manageable crowds across most of the country.",
          },
        ],
      },
      {
        h2: "Sun, Wildlife & Staying Safe",
        body:
          "Australia's UV index is among the highest on earth and sunburn happens in under fifteen minutes in summer. Use SPF 50+, a wide-brim hat, and sunglasses, and reapply after swimming. Wildlife risks are overstated for ordinary travellers but real if ignored: heed crocodile warning signs across the tropical north, never swim in unmarked rivers or estuaries, and shake out shoes left outside overnight in rural areas.",
        subsections: [
          { h3: "Outback Preparation", body: "Carry at least four litres of water per person per day, tell someone your route, and stay with the vehicle if it breaks down — it is far easier to spot than a person on foot." },
          { h3: "Connectivity", body: "Telstra has the widest rural coverage; an eSIM with 20–30GB runs about A$35–$50 for a month. Expect no signal at all across long outback stretches." },
        ],
      },
      {
        h2: "Frequently Asked Questions",
        body:
          "The most common reader questions about planning an Australian trip in 2026.",
        subsections: [
          {
            h3: "Trip design",
            body: "How long to go for and what to leave out.",
            points: [
              { h4: "Is two weeks enough?", body: "Yes for two regions, such as Sydney plus the reef, or Melbourne plus Tasmania. Three weeks lets you add the Red Centre." },
              { h4: "Best month overall?", body: "October and April balance warm southern weather with the dry tropical season in the north." },
              { h4: "Can I see the reef ethically?", body: "Choose small operators with High Standard eco-certification and avoid touching or standing on coral." },
            ],
          },
          {
            h3: "Practicalities",
            body: "Everyday questions on money, driving, and health.",
            points: [
              { h4: "Do I need an IDP?", body: "Not if your licence is in English; otherwise carry an International Driving Permit with the original licence." },
              { h4: "Is healthcare covered?", body: "Only for a handful of countries with reciprocal agreements, and even then partially. Travel insurance is essential." },
              { h4: "Is tap water safe?", body: "Yes across the country, including most remote towns." },
            ],
          },
        ],
      },
    ],
  },
  italy: {
    headline: "Plan Your Trip to Italy: Rome, Tuscany & the Amalfi Coast",
    intro:
      "Italy delivers layered history, world-class food, and dramatic landscapes within a country you can cross by train in a few hours. That density is a trap as much as a gift: the classic first-time mistake is six cities in ten days, arriving everywhere tired and seeing nothing properly. This 2026 guide builds a realistic ten-to-fourteen-day route around Rome, Florence, and Venice, explains ETIAS and Schengen rules, decodes high-speed rail and the ZTL driving zones that catch out visitors, and sets out honest daily budgets in euros.",
    bestTime: "April–June & September–October",
    budget: "€120–€230 / day",
    duration: "10–14 days",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d9/20110720_Verona_3078.jpg/1280px-20110720_Verona_3078.jpg",
    sections: [
      {
        h2: "Schengen Entry Rules & ETIAS",
        body:
          "Italy is part of the Schengen Area, so entry rules are shared across 29 European countries and the 90-days-in-180 clock counts every day spent anywhere in the zone. From 2026 the EU's Entry/Exit System registers biometrics at the border, and visa-exempt travellers additionally need an ETIAS travel authorisation. Neither replaces a passport valid for three months beyond your planned departure.",
        subsections: [
          {
            h3: "ETIAS",
            body:
              "A €7 online authorisation valid three years or until passport expiry, taking around ten minutes to complete and usually approved within minutes. It is a pre-travel screening for visa-exempt nationalities such as the US, UK, Canada, and Australia — not a visa, and not a guarantee of entry.",
            points: [
              { h4: "Under 18 or Over 70", body: "Free of charge, but the application is still required." },
              { h4: "Official Portal Only", body: "Apply on the EU's own site. Commercial intermediaries add fees for the same form." },
            ],
          },
          {
            h3: "Schengen Visa",
            body:
              "Nationalities that need a visa apply at the Italian consulate covering their region, typically 15–45 days ahead, with travel insurance of at least €30,000 medical cover, confirmed accommodation, and proof of funds. The visa allows 90 days in any 180 across the whole Schengen zone.",
            points: [
              { h4: "Main Destination Rule", body: "Apply to the country where you will spend the most nights, or the first point of entry if evenly split." },
              { h4: "Insurance Proof", body: "The €30,000 medical minimum is checked at application and can be checked again at the border." },
            ],
          },
        ],
      },
      {
        h2: "The Classic Two-Week Route",
        body:
          "Rome, Florence, and Venice cover the essentials of ancient, Renaissance, and maritime Italy, and high-speed trains link them in ninety minutes to two hours each. Add one landscape leg — the Amalfi Coast, Cinque Terre, or the Dolomites — and resist the urge for more. Three nights minimum per base keeps travel days from swallowing the trip.",
        subsections: [
          {
            h3: "Rome (3–4 nights)",
            body:
              "The Vatican Museums, Colosseum and Forum, Pantheon, and Trastevere for evening dining. Book the Vatican, Borghese Gallery, and Colosseum underground tickets weeks ahead — walk-up entry in high season means hours of queueing or nothing at all.",
            points: [
              { h4: "Early Entry", body: "First-slot tickets at 8am at the Vatican and Colosseum are worth the early alarm in June to September." },
              { h4: "Dress Code", body: "Covered shoulders and knees are enforced at St Peter's and most major churches." },
            ],
          },
          {
            h3: "Florence & Tuscany (3 nights)",
            body:
              "The Uffizi, Accademia, and Duomo complex fill two days; the third belongs to the countryside. A Chianti wine day or a Siena and San Gimignano loop shows the Tuscany people picture. Renting a car for one day out of Florence is far easier than keeping one in the city.",
            points: [
              { h4: "Museum Reservations", body: "The Uffizi and Accademia require timed slots in peak months; buy directly from the official ticket office online." },
              { h4: "Val d'Orcia", body: "Pienza, Montalcino, and Montepulciano make a superb full-day drive south of Siena." },
            ],
          },
          {
            h3: "Venice (2 nights)",
            body:
              "Stay overnight rather than day-tripping: the city transforms after the cruise crowds leave. Get lost in Cannaregio and Dorsoduro, take the number 1 vaporetto down the Grand Canal at dusk, and remember the day-visitor access fee that now applies on many peak dates.",
            points: [
              { h4: "Access Fee", body: "Day visitors pay a booked entry contribution on designated busy days; overnight guests are exempt but must register." },
              { h4: "Acqua Alta", body: "High water is most likely from October to January. Raised walkways appear, but pack waterproof shoes." },
            ],
          },
        ],
      },
      {
        h2: "Trains, Driving & ZTL Zones",
        body:
          "Trenitalia's Frecciarossa and the private operator Italo run frequent high-speed services between major cities, with advance fares from around €19 and walk-up prices three times higher. Regional trains need no reservation but are slow and occasionally strike-affected. Driving is only worth it for Tuscany, Puglia, and the Dolomites — and city historic centres are protected by ZTL zones where cameras issue fines automatically to unauthorised cars.",
        subsections: [
          { h3: "Booking Rail Smartly", body: "Buy directly from Trenitalia or Italo. Fares open around four months ahead; the cheapest Super Economy tickets are non-refundable but half the flexible price." },
          { h3: "Car Hire Cautions", body: "Confirm with your hotel whether its street lies inside a ZTL and have your plate registered. Fines arrive months later via the rental agency with an administration charge on top." },
        ],
      },
      {
        h2: "Food Culture & Eating Well",
        body:
          "Italian eating has rhythms worth respecting: cappuccino before eleven, an aperitivo from six, dinner from eight. Coperto, the small per-person cover charge, is normal and not a scam, and tipping beyond rounding up is not expected. The best value rule remains simple — eat where the menu is short, seasonal, in Italian, and not translated into five languages outside a major monument.",
        subsections: [
          {
            h3: "Regional Specialities",
            body:
              "Cacio e pepe and carbonara in Rome, bistecca alla fiorentina and ribollita in Florence, cicchetti and seafood risotto in Venice, and pizza in its birthplace in Naples. Ordering the region's dish rather than a national favourite is consistently the better meal.",
            points: [
              { h4: "Lunch Value", body: "Set menù del giorno lunches at €12–€18 often use the same kitchen as a €40 dinner." },
              { h4: "Water and Bread", body: "Bottled water and bread are charged as items; still and sparkling both cost €2–€4." },
            ],
          },
          {
            h3: "Daily Budget",
            body:
              "Plan on €120–€230 per person per day: €80–€150 for a mid-range room, €40–€60 for food, €10–€20 for local transport, and €25–€45 for museums and sites. Rome and Venice run above this range in summer; Puglia, Umbria, and Sicily run comfortably below it.",
          },
        ],
      },
      {
        h2: "Seasons, Crowds & Practicalities",
        body:
          "April to June and September to October give warm days, workable queues, and the best light. July and August are hot — often above 35°C in Rome and Florence — and much of the country goes on holiday in mid-August, when family-run restaurants close for two weeks. Winter is genuinely rewarding in the art cities: no queues, low hotel rates, and Christmas markets in the north.",
        subsections: [
          { h3: "Money and Safety", body: "Cards are accepted almost everywhere and ATMs are common, but keep €30–€50 cash for small cafés. Pickpocketing is the main risk on Rome and Naples transit; use a zipped cross-body bag on crowded buses." },
          { h3: "Connectivity and Plugs", body: "EU eSIMs cost €10–€20 for 10–20GB with excellent coverage. Italy uses 230V with Type C, F, and L sockets — bring an adapter that handles the three-pin L variant." },
        ],
      },
      {
        h2: "Frequently Asked Questions",
        body:
          "Reader questions we answer most often about a first Italian itinerary.",
        subsections: [
          {
            h3: "Itinerary questions",
            body: "How to divide limited days between cities and countryside.",
            points: [
              { h4: "How many cities in ten days?", body: "Three bases maximum. Four means a travel day every second morning." },
              { h4: "Amalfi or Cinque Terre?", body: "Amalfi for dramatic cliffside villages and a slower pace; Cinque Terre for easier rail access and coastal hiking." },
              { h4: "Is a car needed?", body: "No for Rome, Florence, and Venice. Yes for one day in Tuscany or for touring Puglia and the Dolomites." },
            ],
          },
          {
            h3: "Money and logistics",
            body: "Costs, tickets, and everyday etiquette.",
            points: [
              { h4: "Do I tip?", body: "Rounding up is plenty. Coperto already covers the table service." },
              { h4: "Are city taxes extra?", body: "Yes — €2–€10 per person per night, usually collected in cash at the hotel." },
              { h4: "Is tap water drinkable?", body: "Yes, and Rome's street fountains dispense cold, safe drinking water for free." },
            ],
          },
        ],
      },
    ],
  },
  portugal: {
    headline: "Plan Your Trip to Portugal: Lisbon, Porto & the Algarve",
    intro:
      "Portugal offers Europe's best-value coastline, two of its most characterful cities, and wine regions that rival anything in France at a fraction of the price. It is compact, safe, English-friendly, and pleasant almost year-round, which makes it unusually forgiving to plan. This 2026 guide sets out ETIAS and Schengen entry rules, a seven-to-ten-day route through Lisbon, Porto, and one coastal region, real daily budgets in euros, transport choices, food and wine highlights, and the seasonal trade-offs that matter most.",
    bestTime: "May–June & September–October",
    budget: "€90–€180 / day",
    duration: "7–10 days",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/2/24/Alentejo_September_2013-1.jpg/1280px-Alentejo_September_2013-1.jpg",
    sections: [
      {
        h2: "Entry, ETIAS & Long Stays",
        body:
          "Portugal is in the Schengen Area, so the 90-days-in-180 rule counts every day spent anywhere in the zone, not just in Portugal. From 2026, visa-exempt nationalities need an ETIAS authorisation before travelling, and the EU Entry/Exit System records biometrics at the border. EU, EEA, and Swiss travellers continue to enter with a national ID card.",
        subsections: [
          {
            h3: "ETIAS Basics",
            body:
              "A €7 authorisation valid for three years or until passport expiry, tied to the passport used at application. It takes minutes online through the official EU portal and covers unlimited short visits within its validity, subject to the 90/180 limit.",
            points: [
              { h4: "Passport Validity", body: "Three months beyond your intended departure date, and issued within the last ten years." },
              { h4: "Not a Visa", body: "It authorises travel to the border only. Border officers may still ask for return tickets and accommodation." },
            ],
          },
          {
            h3: "Staying Longer Than 90 Days",
            body:
              "Travellers who fall for Portugal often ask about staying on. The D7 passive-income visa and the D8 digital-nomad visa are the two main routes for non-EU nationals, both requiring proof of stable income, accommodation, and health cover, and both applied for from your home country. Our immigration section covers current thresholds in detail.",
            points: [
              { h4: "Plan Ahead", body: "Consulate appointments and AIMA residency steps take months. A tourist visit cannot be converted on the spot." },
              { h4: "Tax Consequences", body: "Spending more than 183 days a year in Portugal generally creates tax residency. Take advice before committing." },
            ],
          },
        ],
      },
      {
        h2: "Where to Go & How Long to Stay",
        body:
          "Combine Lisbon, Porto, and one coastal or inland region for a well-rounded seven to ten days. The Alfa Pendular train links the two cities in under three hours, so no internal flights are needed. Portugal is small enough that day trips do real work here in a way they do not in larger countries.",
        subsections: [
          {
            h3: "Lisbon (3 nights)",
            body:
              "Alfama's tiled lanes and fado houses, Belém's monastery and pastéis de nata, the viewpoints of Graça and Senhora do Monte, and a full-day trip to Sintra's palaces. The city's hills are steep and the pavements slippery when wet — proper shoes matter more than in most European capitals.",
            points: [
              { h4: "Sintra Timing", body: "Buy Pena Palace tickets online for the first slot and travel by train from Rossio to beat the coaches." },
              { h4: "Tram 28", body: "Board at Martim Moniz early morning, or walk the route instead — it is the same views without the crush." },
            ],
          },
          {
            h3: "Porto (2–3 nights)",
            body:
              "The Ribeira waterfront, Livraria Lello, port lodges across the river in Vila Nova de Gaia, and a Douro Valley day that is the highlight of most Portuguese trips. The historic train from São Bento up the Douro line is one of Europe's great cheap rail journeys.",
            points: [
              { h4: "Douro Options", body: "Choose a small-group tour with two quinta visits and a river cruise segment; self-driving conflicts with tasting." },
              { h4: "Port Tasting", body: "Lodge tours run €15–€30 including tastings; Taylor's and Graham's have the best terrace views." },
            ],
          },
          {
            h3: "The Algarve or Alentejo",
            body:
              "Coast lovers head south to Lagos, Sagres, and the Benagil sea caves. Slow-travel seekers prefer inland Alentejo — Évora's Roman temple, the walled hill town of Monsaraz, and cork oak plains with almost no traffic. Both are three hours or less from Lisbon.",
            points: [
              { h4: "Best Beaches", body: "Praia da Marinha, Praia do Camilo, and Praia da Falésia for cliffs and clear water." },
              { h4: "Off-Season Bonus", body: "October in the Algarve is warm, half-empty, and roughly half the August price." },
              { h4: "Surf Coast", body: "Ericeira and Nazaré north of Lisbon offer serious waves and a far less developed feel than the south." },
            ],
          },
        ],
      },
      {
        h2: "Transport & Getting Around",
        body:
          "Alfa Pendular and Intercidades trains connect Lisbon, Coimbra, and Porto quickly and cheaply when booked a few days ahead. Rede Expressos buses reach places trains do not, including much of the Alentejo. A rental car is genuinely useful only for the Algarve coast, the Douro, and inland villages where buses run twice a day.",
        subsections: [
          { h3: "City Transport", body: "Lisbon's metro, trams, and funiculars run on the Navegante card; Porto's metro and buses use Andante. Both are inexpensive, and a 24-hour pass costs about €7." },
          { h3: "Driving Notes", body: "Many motorways use electronic-only tolls; ask the rental company for a transponder to avoid fines. Parking in Lisbon and Porto historic centres is difficult and best avoided altogether." },
        ],
      },
      {
        h2: "Costs, Money & Value",
        body:
          "Portugal remains one of Western Europe's best-value destinations at €90–€180 per person per day. A three-course lunch with wine still costs €12–€18 outside tourist streets, museum entry is typically €5–€15, and intercity rail rarely exceeds €35. Lisbon hotel prices have risen sharply, so shifting one or two nights to Porto, Coimbra, or the Alentejo stretches the budget noticeably.",
        subsections: [
          {
            h3: "Daily Spend",
            body:
              "Expect €60–€110 for a mid-range room, €30–€50 for food and wine, €5–€15 for local transport, and €15–€35 for tours and entries. A city tax of €2–€4 per person per night applies in Lisbon, Porto, and several municipalities.",
            points: [
              { h4: "Best Value Months", body: "May, June, late September, and October give beach-worthy weather without August pricing." },
              { h4: "Tipping", body: "Modest: round up, or leave 5–10% for good restaurant service. Nothing is expected in cafés." },
            ],
          },
          {
            h3: "Weather by Season",
            body:
              "Summers are hot and dry, with Algarve highs above 30°C and the interior hotter still. Winters are mild and green in the south but genuinely wet in Porto and the north, where November to February rainfall is high. Spring and autumn are the sweet spot in every region.",
          },
        ],
      },
      {
        h2: "Food, Wine & Everyday Etiquette",
        body:
          "Portuguese cooking is coastal and unpretentious: grilled sardines, bacalhau in a hundred preparations, cataplana seafood stews, and pastries that justify their reputation. The couvert of bread, olives, and cheese brought unasked is chargeable and can be declined without offence. Portuguese people are warm and formal in equal measure — a greeting on entering a shop is expected, and English is widely spoken in cities and tourist areas.",
        subsections: [
          { h3: "Wine Regions", body: "Douro for reds and port, Alentejo for full-bodied value, Vinho Verde in the north for light summer whites, and Setúbal for Moscatel. Tastings are inexpensive and rarely require booking outside the Douro." },
          { h3: "Safety and Connectivity", body: "Portugal ranks among the world's safest countries; petty theft on Lisbon trams is the main concern. EU eSIMs cost €10–€20 for 10–20GB, and coverage is strong even on rural coast roads." },
        ],
      },
      {
        h2: "Frequently Asked Questions",
        body:
          "The questions readers most often ask before a first trip to Portugal.",
        subsections: [
          {
            h3: "Route and season",
            body: "Deciding when to travel and how to split the days.",
            points: [
              { h4: "Is a week enough?", body: "Yes: three nights Lisbon, three nights Porto, and a day trip from each. Ten days adds the Algarve or Alentejo." },
              { h4: "Lisbon or Porto?", body: "Lisbon for scale, nightlife, and day trips; Porto for atmosphere, wine, and lower prices. Most visitors prefer having both." },
              { h4: "Beach season?", body: "June to September for warm water in the Algarve; the Atlantic north stays cold all year." },
            ],
          },
          {
            h3: "Practical questions",
            body: "Costs, cards, and getting around.",
            points: [
              { h4: "Do I need a car?", body: "Not for the cities. Yes for exploring the Algarve coast or Alentejo villages at your own pace." },
              { h4: "Are cards accepted?", body: "Almost everywhere, though small rural cafés and markets still prefer cash. Carry €30–€50." },
              { h4: "Is tap water safe?", body: "Yes throughout the country; the taste varies by region but it is fully potable." },
            ],
          },
        ],
      },
    ],
  },
};
